import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { ImportJob } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@nucleus-commerce/ui/components/sheet';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { saveFile } from '@/shared/browser/save-file';

import { useImportJob } from '../hooks/use-import-job';
import { rowErrorsToCsv } from '../schemas/row-errors-csv';

import { ImportModeBadge } from './import-mode-badge';
import { JobStatusBadge } from './job-status-badge';

export interface ImportErrorsSheetProps {
  storeId: string;
  jobId: string | null;
  onClose: () => void;
}

/** One import with its row errors (the backend keeps the first 500), downloadable as CSV. */
export function ImportErrorsSheet({ storeId, jobId, onClose }: ImportErrorsSheetProps) {
  const { t } = useTranslation('importsExports');
  const format = useFormat();
  const query = useImportJob(storeId, jobId);
  const job: ImportJob | undefined = query.data;

  const downloadErrors = () => {
    if (!job) return;
    const csv = rowErrorsToCsv(job.errors, [
      t('errors.columns.row'),
      t('errors.columns.field'),
      t('errors.columns.message'),
    ]);
    saveFile(`import-errors-${job.id}.csv`, new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  };

  return (
    <Sheet open={jobId !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <SheetTitle>
              {job ? t('details.title', { kind: t(`kinds.${job.kind}`) }) : t('details.loading')}
            </SheetTitle>
            {job ? <ImportModeBadge job={job} /> : null}
          </div>
          <SheetDescription>
            {job
              ? t('details.summary', {
                  processed: format.number(job.processedRows),
                  total: format.number(job.totalRows),
                  errors: format.number(job.errorRows),
                })
              : ' '}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 px-4 pb-6">
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          ) : !job ? (
            <Skeleton className="h-40" />
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <JobStatusBadge status={job.status} />
                  <span className="text-muted-foreground" title={format.utc(job.createdAt)}>
                    {format.dateTime(job.createdAt)}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={job.errors.length === 0}
                  onClick={downloadErrors}
                >
                  <Download aria-hidden />
                  {t('errors.download')}
                </Button>
              </div>
              {job.errorsTruncated || job.errorRows > job.errors.length ? (
                <p className="text-muted-foreground text-xs">
                  {t('errors.truncated', { shown: job.errors.length, total: job.errorRows })}
                </p>
              ) : null}
              <Table aria-label={t('errors.title')}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16 text-right">{t('errors.columns.row')}</TableHead>
                    <TableHead className="w-40">{t('errors.columns.field')}</TableHead>
                    <TableHead>{t('errors.columns.message')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {job.errors.length > 0 ? (
                    job.errors.map((error, i) => (
                      <TableRow key={`${error.row}-${i}`}>
                        <TableCell className="text-right tabular-nums">{error.row}</TableCell>
                        <TableCell className="font-mono text-xs">{error.field ?? '—'}</TableCell>
                        <TableCell className="whitespace-normal">{error.message}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground h-16 text-center">
                        {t('errors.none')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
